import { Injectable } from '@angular/core'
import { Observable } from 'rxjs/Observable'

import { Folder, FolderService } from 'models'

@Injectable()
export abstract class BaseFolderTreeService {
  protected fetchParams: Object = {}

  protected rootFolder: Folder
  protected openFolderIds: number[] = []
  protected loadingFolderIds: number[] = []

  constructor(protected folderService: FolderService) { }

  isFolderOpen(folderId: number): boolean {
    return this.openFolderIds.indexOf(folderId) > -1
  }

  isFolderLoading(folderId: number): boolean {
    return this.loadingFolderIds.indexOf(folderId) > - 1
  }

  openFolder(folderId: number): Observable<any> {
    if(!this.isFolderOpen(folderId)) {
      this.openFolderIds.push(folderId)
      return this.afterOpenFolder(folderId)
    }
    return Observable.of([])
  }

  closeFolder(folderId: number) {
    let index = this.openFolderIds.indexOf(folderId)
    if(index == -1) { return }
    this.openFolderIds.splice(index, 1)
    this.afterCloseFolder(folderId)
  }

  closeFolderSubtree(folderId: number) {
    this.closeFolder(folderId)
    let folder = this.findFolder(folderId)
    if(folder) {
      folder.subFolders.forEach((subFolder: Folder) => {
        this.closeFolderSubtree(subFolder.id)
      })
    }
  }

  closeAllFolders() {
    if(this.rootFolder) {
      this.closeFolderSubtree(this.rootFolder.id)
    }
  }

  setFetchParams(params: Object) {
    this.fetchParams = params
  }

  getRootFolder(): Folder {
    return this.rootFolder
  }

  findFolder(folderId: number, node: Folder = this.rootFolder): Folder {
    if(node.id == folderId) {
      return node
    } else {
      let i = node.subFolders.length
      while(i--) {
        let result = this.findFolder(folderId, node.subFolders[i])
        if(result) { return result }
      }
      return null
    }
  }

  loadSubFoldersOf(folderId: number): Observable<Folder> {
    let folder = this.findFolder(folderId)
    if(folder) {
      this.markFolderLoading(folderId, true)
      return Observable.create(observer => {
        this.fetchFolderSubTree(folder.id, this.fetchParams).subscribe(
          sourceFolder => {
            this.markFolderLoading(folder.id, false)
            folder.subFolders = sourceFolder.subFolders
            observer.next(folder)
            observer.complete()
          },
          error => {
            this.markFolderLoading(folder.id, false)
            observer.error(error)
          }
        )
      })
    } else {
      return Observable.of(null)
    }
  }

  folderMoveable(folderId: number, targetFolderId: number): boolean {
    if(folderId == targetFolderId) { return false }
    let folder = this.findFolder(folderId)
    let targetFolder = this.findFolder(targetFolderId)
    return !targetFolder.isDescendantOf(folder) && !targetFolder.isParentOf(folder)
  }

  moveFolder(folderId: number, targetFolderId: number): Observable<Folder> {
    let params = { id: folderId, parentId: targetFolderId }
    return Observable.create(observer => {
      this.folderService.update(params).subscribe(
        data => {
          observer.next(data['folder'])
          observer.complete()
        },
        error => { observer.error(error) }
      )
    })
  }

  abstract loadData(): Observable<Object>

  protected markFolderLoading(folderId: number, loading: boolean) {
    let index = this.loadingFolderIds.indexOf(folderId)
    if(loading && index == -1) {
      this.loadingFolderIds.push(folderId)
    }
    else if(!loading && index > -1) {
      this.loadingFolderIds.splice(index, 1)
    } 
  }

  protected loadFolderTree(): Observable<Folder> {
    return Observable.create(observer => {
      this.fetchFolderTree(this.fetchParams).subscribe(
        folder => {
          this.rootFolder = folder
          observer.next(folder)
          observer.complete()
        },
        error => {
          observer.error(error)
        }
      )
    })
  }

  protected abstract fetchFolderTree(params?: Object): Observable<Folder>

  protected abstract fetchFolderSubTree(folderId: number, params?:Object): Observable<Folder>

  protected abstract afterOpenFolder(folderId: number): Observable<any>

  protected abstract afterCloseFolder(folderId: number)
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./app/common/folder-tree/base-folder-tree.service.ts